import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import {
  User, Truck, MapPin, CreditCard, CheckCircle2, ChevronRight, ChevronLeft,
  ShoppingBag, Home, Package, Copy, Check, Upload, FileText, X, ShieldCheck, Store, ArrowLeft, ChevronDown, AlertTriangle
} from 'lucide-react';

import { NavbarPublic } from '../../components/NavbarPublic.jsx';
import { api } from '@/lib/api.ts';
import { formatCurrency } from '../../lib/format.ts';
import { formatLocalCurrency } from '../../lib/dolar.ts';
import { useCarrito } from '../../features/carrito/useCarrito.ts';
import { PAYMENT_METHOD_ICONS, PAYMENT_METHOD_COLORS } from '../../lib/paymentMethods.ts';

// --- FONDO AMBIENTAL ---
const AmbientBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-[#020202]">
    <div className="absolute top-[10%] left-[-10%] w-[60vw] h-[60vw] bg-indigo-900/20 rounded-full blur-[150px] opacity-30 mix-blend-screen" />
    <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-purple-900/10 rounded-full blur-[150px] opacity-20 mix-blend-screen" />
  </div>
);

// --- ESTILOS REUTILIZABLES ---
const inputClass = "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 outline-none transition-all text-sm";
const labelClass = "block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2";
const cardSelectorClass = "flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/5 hover:border-indigo-500/50 hover:bg-indigo-500/10 cursor-pointer transition-all group";
const cardSelectorActiveClass = "border-indigo-500 bg-indigo-500/20 shadow-[0_0_15px_rgba(99,102,241,0.2)]";

const STEPS = [
  { id: 1, name: 'Datos', icon: User },
  { id: 2, name: 'Entrega', icon: Truck },
  { id: 3, name: 'Detalles', icon: MapPin },
  { id: 4, name: 'Pago', icon: CreditCard },
  { id: 5, name: 'Confirmar', icon: CheckCircle2 },
];

export function Checkout() {
  const carrito = useCarrito();
  const { slug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);

  // Estado para dirección calculada (texto para shipping_address)
  const [computedAddress, setComputedAddress] = useState('');

  // ✅ NUEVO: Estado para delivery
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState(null); // {name, price}

  const [formData, setFormData] = useState({
    // Paso 1: Datos personales
    nombre: '', email: '', telefono: '', customer_id_number: '',
    // Paso 2: Método de entrega
    shippingMethod: '',
    // Paso 3: Dirección / Sucursal / Agencia
    direccion: '', agencia: '', direccionAgencia: '', branchId: '',
    // Paso 4: Método de pago
    paymentMethodId: null,
    // Notas
    notas: '',
    // Paso 5: Comprobante de pago
    comprobante: null, numeroReferencia: '', bancoOrigen: '', cedulaPagador: '',
    telefonoPagador: '', emailPagador: '', cuentaOrigen: '',
  });

  // --- FILTRO DE SEGURIDAD ---
  // Solo procesamos items de ESTA tienda
  const currentStoreItems = carrito.items.filter(
    item => item.storeSlug === slug || !item.storeSlug
  );

  const total = currentStoreItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const deliveryFee = selectedDeliveryZone?.price || 0;

  const { register, handleSubmit, setValue, formState: { errors }, reset, watch } = useForm();

  // Observamos el campo de método de pago en tiempo real para el Paso 5
  const watchedPaymentMethodId = watch('paymentMethodId');

  // --- NUEVO: SCROLL AUTOMÁTICO AL CAMBIAR DE PASO ---
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // --- DATA FETCHING ---

  // 1. Obtener Usuario Logueado (AUTO-FILL)
  const { data: userData } = useQuery({
    queryKey: ['current-user-checkout'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/api/auth/me');
        return data;
      } catch (error) {
        return null; // Usuario no logueado
      }
    },
    retry: false
  });

  // 2. Efecto para rellenar datos si existe usuario
  useEffect(() => {
    if (userData) {
      // Preparamos los datos automáticos
      const autoData = {
        nombre: userData.name || '',
        email: userData.email || '',
        telefono: userData.phone || '',
        customer_id_number: userData.id_number || userData.cedula || '',
      };

      // Actualizamos el estado local del formulario
      setFormData(prev => ({ ...prev, ...autoData }));
      
      // Actualizamos los inputs visuales (React Hook Form)
      setValue('nombre', autoData.nombre);
      setValue('email', autoData.email);
      setValue('telefono', autoData.telefono);
      setValue('customer_id_number', autoData.customer_id_number);
    }
  }, [userData, setValue]);

  // 3. Configuración Pública de la Tienda
  const { data: storeData } = useQuery({
    queryKey: ['store-settings-public', slug],
    queryFn: async () => (await api.get(`/api/stores/${slug}/public`)).data,
    staleTime: 1000 * 60 * 5,
  });

  const settings = storeData?.settings || {};
  const branches = storeData?.branches || {};
  const deliveryRates = Array.isArray(settings.delivery_rates) ? settings.delivery_rates : [];

  // ✅ Calcular si el delivery es propio (DESPUÉS de tener settings)
  const isDeliveryOwn = settings.delivery_is_own !== false; // Default true para compatibilidad
  const shouldAddDeliveryToTotal = formData.shippingMethod === 'delivery' && isDeliveryOwn;
  const grandTotal = total + (shouldAddDeliveryToTotal ? deliveryFee : 0);

  // 4. Cálculo de Opciones de Envío Habilitadas
  const shippingOptions = [
    settings.pickup_enabled && { val: 'pickup', label: 'Retiro en Tienda', desc: 'Elige tu sucursal más cercana', icon: Home },
    settings.delivery_enabled && { val: 'delivery', label: 'Delivery Local', desc: 'Envío hasta tu puerta', icon: Truck },
    settings.shipping_enabled && { val: 'national_shipping', label: 'Envío Nacional', desc: 'Agencias de encomienda', icon: Package }
  ].filter(Boolean);

  // 5. Agencias de Envío Configuradas
  const shippingAgencies = Array.isArray(settings.shipping_agencies) ? settings.shipping_agencies : [];

  // 6. Efecto para seleccionar un método de envío válido por defecto
  useEffect(() => {
    if (shippingOptions.length > 0 && !formData.shippingMethod) {
      setFormData(prev => ({ ...prev, shippingMethod: shippingOptions[0].val }));
      setValue('shippingMethod', shippingOptions[0].val);
    }
  }, [shippingOptions.length, settings]);

  // 7. Tasa del Dólar
  const { data: tasaDolar } = useQuery({
    queryKey: ['tasa-dolar', slug],
    queryFn: async () => {
      try {
        if (storeData?.settings?.custom_exchange_rate && parseFloat(storeData.settings.custom_exchange_rate) > 0) {
          return storeData.settings.custom_exchange_rate;
        }
        const { data: bcvData } = await api.get('/api/dolar-bcv/');
        const currency = storeData?.settings?.preferred_currency || 'USD';
        return bcvData[currency] || bcvData.dolar_oficial || bcvData.USD || 50;
      } catch (error) { return 50; }
    },
    enabled: !!storeData
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['payment-methods', slug],
    queryFn: async () => (await api.get(`/api/stores/${slug}/payment-methods`)).data,
  });

  const checkoutMutation = useMutation({
    mutationFn: async (payload) => {
      if (currentStoreItems.length === 0) throw new Error('El carrito de esta tienda está vacío.');
      
      let finalFilePath = null;

      // --- PASO 1: SUBIDA DE IMAGEN CON COMPRESIÓN (PRE-CHECKOUT) ---
      // Solo si el usuario adjuntó un archivo
      if (payload.comprobante && payload.comprobante[0]) {
        try {
          const file = payload.comprobante[0];
          
          // ✅ COMPRIMIR IMAGEN AUTOMÁTICAMENTE
          const compressedFile = await imageCompression(file, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 2000,
            useWebWorker: true,
            quality: 0.9,
            fileType: 'image/jpeg'
          });

          const finalFile = new File([compressedFile], file.name, { type: compressedFile.type });
          
          // A) Pedir URL prefirmada al backend (Nuevo Endpoint)
          const presignResponse = await api.post(`/api/checkout/generate-upload-url/`, {
            fileName: finalFile.name,
            contentType: finalFile.type,
          });
          
          const { uploadURL, filePath } = presignResponse.data;

          // B) Subir archivo COMPRIMIDO a Cloudflare R2 / S3
          const uploadResponse = await fetch(uploadURL, { 
             method: 'PUT', 
             body: finalFile, 
             headers: { 'Content-Type': finalFile.type } 
          });

          if (!uploadResponse.ok) {
             throw new Error(`Error subiendo imagen (R2): ${uploadResponse.status}`);
          }

          // ¡ÉXITO! Guardamos la ruta para enviarla al backend
          finalFilePath = filePath;

        } catch (error) {
          console.error("Fallo subiendo imagen:", error);
          // AQUÍ ESTÁ EL BLINDAJE: Si falla la imagen, LANZAMOS ERROR.
          // El código se detiene aquí y NO crea el pedido fantasma.
          throw new Error("No se pudo subir el comprobante de pago. Por favor verifica tu conexión e intenta nuevamente.");
        }
      }

      // --- PASO 2: CREACIÓN DEL PEDIDO (SOLO SI PASO 1 FUE OK) ---
      
      // Preparar items de forma segura (Fix para IDs numéricos)
      const itemsToSend = currentStoreItems.map((item) => ({
        product_id: parseInt(String(item.id).split('-')[0], 10), 
        quantity: item.quantity,
        variant_attributes: item.variant_attributes || null,
      }));

      const requestData = {
        slug,
        nombre: payload.nombre,
        email: payload.email,
        telefono: payload.telefono,
        customer_id_number: payload.customer_id_number || '',

        // Logística
        shipping_method: payload.shipping_method,
        shipping_address: payload.shipping_method === 'pickup' ? computedAddress : (payload.shipping_address || ''),
        branch_id: payload.shipping_method === 'pickup' ? payload.branchId : null,
        shipping_agency: payload.shipping_agency || '',
        shipping_agency_address: payload.shipping_agency_address || '',
        
        // ✅ NUEVO: Delivery zone y fee
        delivery_zone: selectedDeliveryZone?.name || '',
        delivery_fee: selectedDeliveryZone?.price || 0,

        // Pago - AQUÍ ESTÁ LA CORRECCIÓN: Usamos los nombres que el Backend espera, sacados de las variables del Frontend (camelCase)
        payment_method_id: payload.paymentMethodId || null,
        notas: payload.notas || '',
        numero_referencia: payload.numeroReferencia || '', // Backend: numero_referencia <- Frontend: numeroReferencia
        banco_origen: payload.bancoOrigen || '',           // Backend: banco_origen <- Frontend: bancoOrigen
        cedula_pagador: payload.cedulaPagador || '',       // Backend: cedula_pagador <- Frontend: cedulaPagador
        telefono_pagador: payload.telefonoPagador || '',   // Backend: telefono_pagador <- Frontend: telefonoPagador
        email_pagador: payload.emailPagador || '',         // Backend: email_pagador <- Frontend: emailPagador
        cuenta_origen: payload.cuentaOrigen || '',
        
        // --- CAMPO CLAVE: Enviamos la ruta de la imagen ya subida ---
        payment_receipt: finalFilePath, 
        
        items: itemsToSend,
      };

      const { data } = await api.post('/api/carrito/checkout', requestData);
      
      // Retornamos los datos para el onSuccess
      return data;
    },
    onSuccess: (data) => {
      // Si llegamos aquí, ES UN ÉXITO TOTAL (Pedido + Imagen garantizados)
      const customerEmail = formData.email;
      const orderId = data.id || data.order_id;
      
      // Limpiar carrito
      currentStoreItems.forEach(item => {
          carrito.removeItem(item.id, slug);
      });
      
      // Invalidar queries EXISTENTES
      queryClient.invalidateQueries({ queryKey: ['global-cart-count-nav'] });
      queryClient.invalidateQueries({ queryKey: ['mis-carritos-pendientes'] });
      queryClient.invalidateQueries({ queryKey: ['global-carts-drawer'] });

      // --- FIX CRÍTICO STOCK: Forzar recarga del catálogo ---
      // Al invalidar, React Query volverá a pedir los datos al backend (que ya no tiene caché)
      // y el stock aparecerá actualizado (18 en vez de 19).
      queryClient.invalidateQueries({ queryKey: ['productos'] });
      queryClient.invalidateQueries({ queryKey: ['public-products'] });
      // Invalidación agresiva por SLUG para asegurar que la tienda pública se actualice
      queryClient.invalidateQueries({ predicate: (query) => 
        Array.isArray(query.queryKey) && query.queryKey.includes(slug) 
      });

      // MOSTRAR MODAL DE ÉXITO SIEMPRE
      setShowSuccessModal(true);
      
      // Redirigir después de unos segundos
      setTimeout(() => {
        navigate(`/t/${slug}/orden/${orderId}?email=${encodeURIComponent(customerEmail)}`);
      }, 3000);
    },
    onError: (error) => {
      // Mostramos el error exacto (ej: "No se pudo subir el comprobante...")
      toast.error(error.message || 'Error al procesar el pedido.');
    },
  });

  const onSubmitStep = (values) => {
    // Actualizamos el estado general con lo que viene del paso actual
    const updatedFormData = { ...formData, ...values };
    setFormData(updatedFormData);

    // Si seleccionó sucursal en Paso 3 (pickup), guardamos su dirección como computedAddress
    if (currentStep === 3 && values.shippingMethod === 'pickup' && values.branchId) {
      const selectedBranch = branches.find(b => b.id === Number(values.branchId));
      if (selectedBranch) {
        setComputedAddress(`RETIRO EN TIENDA: ${selectedBranch.name} - ${selectedBranch.address}`);
      }
    } else if (currentStep === 3 && values.shippingMethod === 'delivery') {
      setComputedAddress(values.direccion);
    }

    // Lógica de navegación
    if (currentStep === 1) { setCurrentStep(2); }
    else if (currentStep === 2) {
      setCurrentStep(3); // Siempre vamos al paso 3 para detalles
    }
    else if (currentStep === 3) { setCurrentStep(4); }
    else if (currentStep === 4) { setCurrentStep(5); }
    else if (currentStep === 5) {
      const finalPayload = {
        ...updatedFormData,
        shipping_address: values.direccion || '',
        shipping_agency: values.agencia || '',
        shipping_agency_address: values.direccionAgencia || '',
        shipping_method: updatedFormData.shippingMethod,
        branchId: updatedFormData.branchId,
        payment_method_id: values.paymentMethodId || updatedFormData.paymentMethodId,
      };
      setPendingFormData(finalPayload);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmOrder = () => {
    setShowConfirmModal(false);
    if (pendingFormData) checkoutMutation.mutate(pendingFormData);
  };

  const goBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  // Empty State (Usamos currentStoreItems para verificar si hay items de ESTA tienda)
  if (currentStoreItems.length === 0 && !showSuccessModal) {
    return (
      <div className="min-h-screen text-slate-200 font-sans selection:bg-indigo-500/30 flex flex-col">
        <AmbientBackground />
        <NavbarPublic />
        <main className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="w-24 h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6">
            <ShoppingBag className="h-10 w-10 text-white/30" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Carrito Vacío</h1>
          <p className="text-white/40 mb-8">Agrega productos antes de procesar.</p>
          <button onClick={() => navigate(`/t/${slug}`)} className="px-8 py-3 rounded-full bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-slate-200 transition-colors">
            Ir a la Tienda
          </button>
        </main>
      </div>
    );
  }

  // Calculamos cuál ID usar para el paso 5 (prioridad al watch en tiempo real)
  const activePaymentMethodId = watchedPaymentMethodId || formData.paymentMethodId;

  // Calculamos el total en Bs para el Sidebar
  const totalBs = tasaDolar ? total * tasaDolar : null;
  const localCurrency = storeData?.settings?.preferred_currency || 'VES';

  return (
    <div className="min-h-screen text-slate-200 font-sans selection:bg-indigo-500/30 flex flex-col pb-12">
      <AmbientBackground />
      <NavbarPublic />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 pt-32">

        {/* --- NUEVO BOTÓN: VOLVER AL CARRITO --- */}
        <Link 
          to={`/t/${slug}/carrito`} 
          className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          Volver al Carrito
        </Link>

        {/* Header & Stepper */}
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-white tracking-tight mb-8 text-center">Finalizar Compra</h1>

          <div className="flex items-center justify-between max-w-3xl mx-auto relative">
            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-white/10 -z-10 -translate-y-1/2" />
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              return (
                <div key={step.id} className="flex flex-col items-center gap-3 bg-[#020202] px-2 z-10">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 border-2 ${isActive ? 'bg-indigo-600 border-indigo-500 shadow-[0_0_15px_rgba(79,70,229,0.5)] scale-110' :
                    isCompleted ? 'bg-emerald-500 border-emerald-500 text-black' :
                      'bg-black border-white/20 text-white/30'
                    }`}
                  >
                    {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${isActive ? 'text-white' : 'text-white/30'}`}>
                    {step.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr,380px]">

          {/* COLUMNA IZQUIERDA: FORMULARIO */}
          <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/60 backdrop-blur-xl p-8 shadow-2xl">
            <form onSubmit={handleSubmit(onSubmitStep)} className="space-y-8">

              {currentStep === 1 && <Step1PersonalInfo register={register} errors={errors} defaultValues={formData} />}

              {currentStep === 2 && (
                <Step2ShippingMethod
                  register={register}
                  defaultValues={formData}
                  options={shippingOptions}
                />
              )}

              {currentStep === 3 && (
                <Step3Details
                  register={register}
                  errors={errors}
                  shippingMethod={formData.shippingMethod}
                  agencies={shippingAgencies}
                  branches={branches}
                  defaultValues={formData}
                  setValue={setValue}
                  deliveryRates={deliveryRates}
                  setSelectedDeliveryZone={setSelectedDeliveryZone}
                  storeData={storeData}
                  selectedDeliveryZone={selectedDeliveryZone}
                />
              )}

              {currentStep === 4 && <Step4PaymentMethod register={register} errors={errors} paymentMethods={paymentMethods} defaultValues={formData} />}
              
              {currentStep === 5 && <Step5PaymentConfirmation register={register} errors={errors} paymentMethods={paymentMethods} selectedPaymentMethodId={activePaymentMethodId} defaultValues={formData} />}

              {/* Botones */}
              <div className="flex items-center justify-between pt-8 border-t border-white/10">
                {currentStep > 1 ? (
                  <button type="button" onClick={goBack} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/50 hover:text-white transition-colors">
                    <ChevronLeft className="h-4 w-4" /> Atrás
                  </button>
                ) : <div />}

                <button
                  type="submit"
                  disabled={checkoutMutation.isPending || (currentStep === 2 && shippingOptions.length === 0)}
                  className="flex items-center gap-2 px-8 py-3 rounded-full bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-indigo-50 transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutMutation.isPending ? 'Procesando...' : currentStep === 5 ? 'Confirmar Pedido' : 'Siguiente'}
                  {!checkoutMutation.isPending && <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </form>
          </div>

          {/* COLUMNA DERECHA: RESUMEN */}
          <aside className="lg:sticky lg:top-32 h-fit space-y-6">
            <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-xl p-6 shadow-xl">
              <h2 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-6 border-b border-white/5 pb-4">Resumen de Orden</h2>
              <div className="space-y-4 mb-6">
                {/* USAMOS EL FILTRO AQUÍ: currentStoreItems */}
                {currentStoreItems.map((item) => (
                  <div key={item.id} className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white truncate">{item.name}</p>
                      <p className="text-[10px] text-white/40">Cant: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                        <span className="text-sm font-mono text-white/70 block">
                            {formatCurrency(item.price * item.quantity)}
                        </span>
                        {tasaDolar && (
                            <span className="text-[10px] font-mono text-emerald-400">
                                {formatLocalCurrency(item.price * item.quantity * tasaDolar, localCurrency)}
                            </span>
                        )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* TOTALES */}
              <div className="border-t border-white/5 pt-4 space-y-2">
                 <div className="flex justify-between items-center text-sm">
                    <span className="text-white/60">Subtotal</span>
                    <span className="text-white">{formatCurrency(total)}</span>
                 </div>
                 
                 {/* ✅ Línea de delivery */}
                 {selectedDeliveryZone && (
                    <>
                      <div className="flex justify-between items-center text-sm">
                         <span className="text-white/60">Delivery ({selectedDeliveryZone.name})</span>
                         <div className="text-right">
                           <span className="text-white block">
                              {Number(selectedDeliveryZone.price) === 0 ? 'GRATIS' : 
                               !isDeliveryOwn ? `${formatCurrency(selectedDeliveryZone.price)} *` :
                               `+${formatCurrency(selectedDeliveryZone.price)}`}
                           </span>
                           {tasaDolar && Number(selectedDeliveryZone.price) > 0 && (
                             <span className="text-[10px] font-mono text-emerald-400">
                               {!isDeliveryOwn && '* '}{formatLocalCurrency(selectedDeliveryZone.price * tasaDolar, localCurrency)}
                             </span>
                           )}
                         </div>
                      </div>
                      
                      {/* Nota para delivery tercerizado */}
                      {!isDeliveryOwn && Number(selectedDeliveryZone.price) > 0 && (
                        <div className="text-[10px] text-yellow-400/80 italic flex items-start gap-1 mt-1">
                          <span>*</span>
                          <span>Pago directo al repartidor</span>
                        </div>
                      )}
                    </>
                 )}
                 
                 <div className="border-t border-white/5 pt-2 flex justify-between items-center">
                    <span className="text-sm font-bold text-white">Total</span>
                    <span className="text-xl font-light text-white">{formatCurrency(grandTotal)}</span>
                 </div>
                 {tasaDolar && (
                    <div className="flex justify-between items-center mt-1">
                         <span className="text-[10px] text-white/30 uppercase">Aprox. en Moneda Local</span>
                         <span className="text-sm font-mono text-emerald-400">{formatLocalCurrency(grandTotal * tasaDolar, localCurrency)}</span>
                    </div>
                 )}
              </div>

              <div className="mt-6 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                <p className="text-[10px] text-indigo-300 font-mono">Paso {currentStep} / 5</p>
                <div className="w-full h-1 bg-indigo-500/20 rounded-full mt-2">
                  <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${(currentStep / 5) * 100}%` }} />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/20">
              <ShieldCheck className="h-4 w-4" /> Transacción Segura
            </div>
          </aside>
        </div>
      </main>

      {showConfirmModal && <ConfirmOrderModal onConfirm={handleConfirmOrder} onCancel={() => setShowConfirmModal(false)} total={total} tasaDolar={tasaDolar} localCurrency={localCurrency} />}
      {showSuccessModal && <SuccessModal />}
    </div>
  );
}

// --- SUB-COMPONENTES ---

function Step1PersonalInfo({ register, errors, defaultValues }) {
  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-500/20 border border-indigo-500/30"><User className="h-5 w-5 text-indigo-400" /></div>
        <h2 className="text-lg font-bold text-white">Datos Personales</h2>
      </div>
      <div className="space-y-5">
        <div>
          <label className={labelClass}>Nombre Completo *</label>
          <input type="text" defaultValue={defaultValues.nombre} className={inputClass} {...register('nombre', { required: 'Requerido' })} />
          {errors.nombre && <p className="text-xs text-red-400 mt-1">{errors.nombre.message}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass}>Email *</label>
            <input type="email" defaultValue={defaultValues.email} className={inputClass} {...register('email', { required: 'Requerido' })} />
            {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <label className={labelClass}>Teléfono *</label>
            <input type="tel" defaultValue={defaultValues.telefono} className={inputClass} {...register('telefono', { required: 'Requerido' })} />
            {errors.telefono && <p className="text-xs text-red-400 mt-1">{errors.telefono.message}</p>}
          </div>
        </div>
        <div>
          <label className={labelClass}>Cédula (Opcional)</label>
          <input type="text" defaultValue={defaultValues.customer_id_number} className={inputClass} {...register('customer_id_number')} />
        </div>
      </div>
    </div>
  );
}

function Step2ShippingMethod({ register, defaultValues, options }) {
  if (!options || options.length === 0) {
    return (
      <div className="animate-in fade-in slide-in-from-right-4 duration-300 text-center py-8">
        <div className="p-4 rounded-full bg-red-500/10 inline-flex mb-4"><Truck className="h-8 w-8 text-red-400" /></div>
        <h3 className="text-white font-bold text-lg mb-2">Sin métodos de envío</h3>
        <p className="text-white/50 text-sm">Esta tienda no tiene métodos disponibles actualmente.</p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-500/20 border border-indigo-500/30"><Truck className="h-5 w-5 text-indigo-400" /></div>
        <h2 className="text-lg font-bold text-white">Método de Entrega</h2>
      </div>
      <div className="space-y-3">
        {options.map((opt) => (
          <label key={opt.val} className={cardSelectorClass}>
            <input type="radio" value={opt.val} defaultChecked={defaultValues.shippingMethod === opt.val} className="w-4 h-4 text-indigo-500 bg-black border-white/20 focus:ring-indigo-500 focus:ring-offset-black" {...register('shippingMethod', { required: true })} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <opt.icon className="h-4 w-4 text-indigo-400" />
                <span className="font-bold text-white text-sm">{opt.label}</span>
              </div>
              <p className="text-xs text-white/40 mt-0.5">{opt.desc}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function Step3Details({ register, errors, shippingMethod, agencies, branches, defaultValues, setValue, deliveryRates, setSelectedDeliveryZone, storeData, selectedDeliveryZone }) {
  const getHeader = () => {
    if (shippingMethod === 'pickup') return { title: 'Selecciona Sucursal', icon: Store };
    if (shippingMethod === 'delivery') return { title: 'Dirección de Entrega', icon: MapPin };
    return { title: 'Datos de Encomienda', icon: Package };
  };

  const { title, icon: Icon } = getHeader();

  if (!shippingMethod) return <div className="text-white/40">Selecciona un método en el paso anterior.</div>;

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-500/20 border border-indigo-500/30"><Icon className="h-5 w-5 text-indigo-400" /></div>
        <h2 className="text-lg font-bold text-white">{title}</h2>
      </div>

      {shippingMethod === 'pickup' && (
        <div className="space-y-4">
          {branches.length > 0 ? (
            branches.map((branch) => (
              <label key={branch.id} className={cardSelectorClass}>
                <input
                  type="radio"
                  value={branch.id}
                  defaultChecked={defaultValues.branchId == branch.id}
                  className="w-4 h-4 text-indigo-500 bg-black border-white/20 focus:ring-indigo-500 focus:ring-offset-black"
                  {...register('branchId', { required: 'Selecciona una sucursal' })}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-white text-sm">{branch.name}</span>
                  </div>
                  <p className="text-xs text-white/60 mt-1">{branch.address}</p>
                  {branch.phone && <p className="text-[10px] text-white/40 mt-1">Tel: {branch.phone}</p>}
                </div>
              </label>
            ))
          ) : (
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-200 text-sm">
              No hay sucursales activas, pero puedes continuar.
            </div>
          )}
          {errors.branchId && <p className="text-xs text-red-400 mt-1">{errors.branchId.message}</p>}
        </div>
      )}

      {shippingMethod === 'delivery' && (
        <div className="space-y-5">
          {/* ✅ NUEVO: Dropdown de zonas de delivery */}
          {deliveryRates && deliveryRates.length > 0 && (
            <div>
              <label className={labelClass}>Zona de Delivery *</label>
              <select 
                className={`${inputClass} cursor-pointer`}
                onChange={(e) => {
                  const zone = deliveryRates.find(r => r.name === e.target.value);
                  setSelectedDeliveryZone(zone || null);
                }}
                defaultValue=""
              >
                <option value="">Selecciona tu zona...</option>
                {deliveryRates.map((rate, idx) => (
                  <option key={idx} value={rate.name}>
                    {rate.name} - ${Number(rate.price) === 0 ? 'GRATIS' : rate.price}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* ✅ NOTA AMARILLA: Pago al Repartidor (Delivery Tercerizado) */}
          {storeData?.settings?.delivery_is_own === false && selectedDeliveryZone && Number(selectedDeliveryZone.price) > 0 && (
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-yellow-200 mb-1">Pago al Repartidor</p>
                <p className="text-xs text-yellow-300/80">
                  El monto del delivery (${selectedDeliveryZone.price}) se paga directamente al repartidor al recibir el producto.
                </p>
              </div>
            </div>
          )}
          
          <div>
            <label className={labelClass}>Dirección Exacta *</label>
            <textarea rows={3} placeholder="Av. Principal, Edificio, Apartamento, Punto de referencia..." defaultValue={defaultValues.direccion} className={`${inputClass} resize-none`} {...register('direccion', { required: 'Requerido' })} />
            {errors.direccion && <p className="text-xs text-red-400 mt-1">{errors.direccion.message}</p>}
          </div>
        </div>
      )}

      {shippingMethod === 'national_shipping' && (
        <div className="space-y-5">
          <div>
            <label className={labelClass}>Agencia de Envío *</label>
            {/* --- FIX VISUAL: Dropdown personalizado --- */}
            <div className="relative">
                <select 
                    defaultValue={defaultValues.agencia} 
                    className={`${inputClass} appearance-none cursor-pointer pr-10`} 
                    {...register('agencia', { required: 'Selecciona agencia' })}
                >
                    <option value="" className="bg-black text-white/50">Seleccionar...</option>
                    {agencies.length > 0 ? (
                        agencies.map((a) => <option key={a} value={a} className="bg-black text-white">{a}</option>)
                    ) : (
                        <option value="GENERICO" className="bg-black text-white">Agencia Genérica</option>
                    )}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/50">
                    <ChevronDown className="h-4 w-4" />
                </div>
            </div>
            {errors.agencia && <p className="text-xs text-red-400 mt-1">{errors.agencia.message}</p>}
          </div>
          <div>
            <label className={labelClass}>Datos de la Agencia Destino *</label>
            <textarea rows={3} placeholder="Estado, Ciudad, Nombre de la oficina de encomiendas..." defaultValue={defaultValues.direccionAgencia} className={`${inputClass} resize-none`} {...register('direccionAgencia', { required: 'Requerido' })} />
            {errors.direccionAgencia && <p className="text-xs text-red-400 mt-1">{errors.direccionAgencia.message}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function Step4PaymentMethod({ register, errors, paymentMethods, defaultValues }) {
  const [selectedId, setSelectedId] = useState(defaultValues.paymentMethodId);
  const [copied, setCopied] = useState(null);
  const selected = paymentMethods.find(m => m.id === Number(selectedId));

  const copy = (txt, key) => {
    navigator.clipboard.writeText(txt);
    setCopied(key);
    toast.success('Copiado');
    setTimeout(() => setCopied(null), 2000);
  };

  const handleCopyAll = () => {
    if (!selected || !selected.fields) return;
    
    const textToCopy = Object.entries(selected.fields)
      .map(([key, value]) => `${key.replace(/_/g, ' ').toUpperCase()}: ${value}`)
      .join('\n');
    
    navigator.clipboard.writeText(textToCopy);
    toast.success('Todos los datos copiados');
  };

  // --- FIX CRÍTICO: Separamos el registro del formulario y el evento visual ---
  const { onChange: onFormChange, ...restAuth } = register('paymentMethodId', { required: 'Requerido' });

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-500/20 border border-indigo-500/30"><CreditCard className="h-5 w-5 text-indigo-400" /></div>
        <h2 className="text-lg font-bold text-white">Método de Pago</h2>
      </div>

      <div className="space-y-3">
        {paymentMethods.map(method => {
          const Icon = PAYMENT_METHOD_ICONS[method.method_type] || CreditCard;
          const grad = PAYMENT_METHOD_COLORS[method.method_type] || 'from-gray-700 to-gray-900';
          const isSel = Number(selectedId) === method.id;

          return (
            <label key={method.id} className={`${cardSelectorClass} ${isSel ? cardSelectorActiveClass : ''}`}>
              <input
                type="radio"
                value={method.id}
                defaultChecked={defaultValues.paymentMethodId === method.id}
                className="hidden"
                // Aplicamos las props del registro manualmente para no sobrescribir onChange
                {...restAuth}
                onChange={(e) => {
                  onFormChange(e); // Actualiza react-hook-form (Estado interno)
                  setSelectedId(e.target.value); // Actualiza estado visual local
                }}
              />
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${grad} flex items-center justify-center shadow-lg`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <span className="font-bold text-white text-sm block">{method.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-white/40">{method.method_type_display}</span>
              </div>
              {isSel && <div className="w-3 h-3 rounded-full bg-indigo-500 shadow-[0_0_8px_#6366f1]" />}
            </label>
          );
        })}
      </div>
      {errors.paymentMethodId && <p className="text-xs text-red-400 mt-1">{errors.paymentMethodId.message}</p>}

      {selected && Object.keys(selected.fields || {}).length > 0 && (
        <div className="mt-6 p-5 rounded-2xl border border-indigo-500/30 bg-indigo-500/10 animate-in zoom-in-95 relative">
          
          <div className="flex justify-between items-center mb-4">
             <h3 className="text-xs font-bold uppercase tracking-widest text-indigo-300 flex items-center gap-2">
                <CreditCard className="h-3 w-3" /> Datos de Pago
             </h3>
             {/* BOTÓN COPIAR TODO */}
             <button 
               type="button" 
               onClick={handleCopyAll}
               className="text-[10px] bg-indigo-500/20 hover:bg-indigo-500/40 text-indigo-300 px-3 py-1.5 rounded-lg transition-colors border border-indigo-500/30 flex items-center gap-1.5"
             >
                <Copy className="h-3 w-3" /> COPIAR TODO
             </button>
          </div>

          <div className="space-y-3">
            {Object.entries(selected.fields).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between p-3 rounded-xl bg-black/40 border border-white/5">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">{k.replace(/_/g, ' ')}</p>
                  <p className="text-sm font-mono text-white select-all">{v}</p>
                </div>
                <button type="button" onClick={() => copy(v, k)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors">
                  {copied === k ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <label className={labelClass}>Notas Adicionales (Opcional)</label>
        <textarea rows={3} defaultValue={defaultValues.notas} className={`${inputClass} resize-none`} {...register('notas')} />
      </div>
    </div>
  );
}

function Step5PaymentConfirmation({ register, errors, paymentMethods, selectedPaymentMethodId, defaultValues }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const selected = paymentMethods.find(m => m.id === Number(selectedPaymentMethodId));
  const type = selected?.method_type;

  // Lógica de previsualización de imagen (Local)
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Limpieza de memoria
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const reqFields = (() => {
    switch (type) {
      case 'pago_movil': return ['numeroReferencia', 'bancoOrigen', 'cedulaPagador', 'telefonoPagador'];
      case 'transferencia': return ['numeroReferencia', 'bancoOrigen', 'cedulaPagador'];
      case 'binance': return ['comprobante'];
      case 'zelle': return ['comprobante', 'emailPagador'];
      default: return ['comprobante'];
    }
  })();

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-500/20 border border-indigo-500/30"><CheckCircle2 className="h-5 w-5 text-indigo-400" /></div>
        <div>
          <h2 className="text-lg font-bold text-white">Confirmar Pago</h2>
          <p className="text-xs text-white/40 mt-0.5">Método: <span className="text-white">{selected?.name}</span></p>
        </div>
      </div>

      <div className="mb-6">
        <label className={labelClass}>Comprobante (Imagen/PDF)</label>
        <div className="relative group cursor-pointer">
          <input 
            type="file" 
            accept="image/*,application/pdf" 
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            {...register('comprobante', { onChange: (e) => setFile(e.target.files[0]) })} 
          />
          
          <div className={`min-h-[160px] rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-all overflow-hidden relative ${file ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/10 bg-black/20 group-hover:border-white/20'}`}>
            
            {file ? (
              previewUrl && file.type.startsWith('image/') ? (
                 <>
                   <img src={previewUrl} alt="Preview" className="w-full h-full object-contain max-h-[300px] absolute inset-0 z-0 p-2" />
                   <div className="absolute inset-0 bg-black/60 z-0 group-hover:bg-black/40 transition-colors" />
                   
                   {/* Botón X para eliminar */}
                   <button
                     type="button"
                     onClick={(e) => {
                       e.stopPropagation();
                       setFile(null);
                       setValue('comprobante', null);
                     }}
                     className="absolute top-2 right-2 z-20 p-2 rounded-full bg-red-500/80 hover:bg-red-500 text-white transition-colors"
                   >
                     <X className="h-4 w-4" />
                   </button>
                   
                   <div className="text-center relative z-10">
                      <FileText className="h-8 w-8 text-emerald-400 mx-auto mb-2 drop-shadow-md" />
                      <p className="text-xs text-emerald-300 font-bold drop-shadow-md">{file.name}</p>
                      <p className="text-[10px] text-emerald-400/80 mt-1">Clic para cambiar</p>
                   </div>
                 </>
              ) : (
                <div className="text-center relative">
                    {/* Botón X para eliminar PDF */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        setValue('comprobante', null);
                      }}
                      className="absolute -top-2 -right-2 z-20 p-2 rounded-full bg-red-500/80 hover:bg-red-500 text-white transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    
                    <FileText className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                    <p className="text-xs text-emerald-300 font-bold">{file.name}</p>
                </div>
              )
            ) : (
              <div className="text-center text-white/30 group-hover:text-white/50">
                <Upload className="h-8 w-8 mx-auto mb-2" />
                <p className="text-xs font-bold uppercase tracking-widest">Subir Archivo</p>
                <p className="text-[10px] mt-1">Click o arrastra aquí</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {reqFields.includes('numeroReferencia') && (
          <div><label className={labelClass}>Nro. Referencia *</label><input type="text" defaultValue={defaultValues.numeroReferencia} className={inputClass} {...register('numeroReferencia', { required: 'Requerido' })} /></div>
        )}
        {reqFields.includes('bancoOrigen') && (
          <div>
            <label className={labelClass}>Banco Origen *</label>
            <select defaultValue={defaultValues.bancoOrigen} className={inputClass} {...register('bancoOrigen', { required: 'Requerido' })}>
              <option value="">Selecciona tu banco</option>
              <option value="Banco de Venezuela (0102)">Banco de Venezuela (0102)</option>
              <option value="BBVA Provincial (0108)">BBVA Provincial (0108)</option>
              <option value="Banesco (0134)">Banesco (0134)</option>
              <option value="Mercantil (0105)">Mercantil (0105)</option>
              <option value="Banco Nacional de Crédito (0191)">Banco Nacional de Crédito - BNC (0191)</option>
              <option value="Bancaribe (0114)">Bancaribe (0114)</option>
              <option value="Bancamiga (0172)">Bancamiga (0172)</option>
              <option value="Banplus (0174)">Banplus (0174)</option>
              <option value="Banco Venezolano de Crédito (0104)">Banco Venezolano de Crédito - BVC (0104)</option>
              <option value="Exterior (0115)">Exterior (0115)</option>
              <option value="100% Banco (0156)">100% Banco (0156)</option>
              <option value="Del Sur (0157)">Del Sur (0157)</option>
              <option value="Banco Activo (0171)">Banco Activo (0171)</option>
              <option value="Sofitasa (0137)">Sofitasa (0137)</option>
              <option value="Banco Plaza (0138)">Banco Plaza (0138)</option>
              <option value="Banco del Tesoro (0163)">Banco del Tesoro (0163)</option>
              <option value="Banco Agrícola de Venezuela (0166)">Banco Agrícola de Venezuela (0166)</option>
              <option value="BANFANB (0177)">BANFANB (0177)</option>
              <option value="Bicentenario (0175)">Bicentenario (0175)</option>
              <option value="Bangente (0146)">Bangente (0146)</option>
              <option value="Bancrecer (0168)">Bancrecer (0168)</option>
              <option value="Mi Banco (0169)">Mi Banco (0169)</option>
            </select>
          </div>
        )}
        {reqFields.includes('cedulaPagador') && (
          <div><label className={labelClass}>Cédula Titular *</label><input type="text" defaultValue={defaultValues.cedulaPagador} className={inputClass} {...register('cedulaPagador', { required: 'Requerido' })} /></div>
        )}
        {reqFields.includes('telefonoPagador') && (
          <div><label className={labelClass}>Teléfono Pago *</label><input type="tel" defaultValue={defaultValues.telefonoPagador} className={inputClass} {...register('telefonoPagador', { required: 'Requerido' })} /></div>
        )}
        {reqFields.includes('emailPagador') && (
          <div><label className={labelClass}>Email Zelle/Pago *</label><input type="email" defaultValue={defaultValues.emailPagador} className={inputClass} {...register('emailPagador', { required: 'Requerido' })} /></div>
        )}
      </div>
    </div>
  );
}

function ConfirmOrderModal({ onConfirm, onCancel, total, tasaDolar, localCurrency = 'VES' }) {
  const totalBs = tasaDolar ? (total * tasaDolar).toFixed(2) : null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-[#0F0F0F] border border-white/10 rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center relative overflow-hidden animate-in zoom-in-95">
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-[60px] pointer-events-none" />

        <div className="w-16 h-16 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mx-auto mb-6">
          <ShoppingBag className="h-8 w-8 text-indigo-400" />
        </div>

        <h2 className="text-xl font-bold text-white mb-2">¿Confirmar Orden?</h2>
        <p className="text-white/40 text-xs mb-6">Estás a un paso de completar tu compra.</p>

        <div className="bg-black/40 rounded-xl p-4 border border-white/5 mb-8">
          <p className="text-xs text-white/50 uppercase tracking-widest mb-1">Total a Pagar</p>
          <p className="text-3xl font-light text-white tracking-tight">{formatCurrency(total)}</p>
          {totalBs && <p className="text-sm font-mono text-emerald-400 mt-1">{formatLocalCurrency(Number(totalBs), localCurrency)}</p>}
        </div>

        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-3 rounded-xl border border-white/10 text-white/60 text-xs font-bold uppercase tracking-widest hover:bg-white/5 transition-colors">Cancelar</button>
          <button onClick={onConfirm} className="flex-1 py-3 rounded-xl bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-gray-200 transition-colors">Sí, Comprar</button>
        </div>
      </div>
    </div>
  )
}

function SuccessModal() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-300">
      <div className="text-center">
        <div className="relative w-24 h-24 mx-auto mb-8">
          <div className="absolute inset-0 bg-emerald-500 rounded-full animate-ping opacity-20" />
          <div className="relative w-full h-full bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(16,185,129,0.4)]">
            <Check className="h-10 w-10 text-white animate-in zoom-in duration-500" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">¡Pedido Recibido!</h2>
        <p className="text-white/50 text-sm">Tu orden ha sido procesada correctamente.</p>
        <p className="text-emerald-400 text-xs uppercase tracking-widest mt-8 animate-pulse">Redirigiendo...</p>
      </div>
    </div>
  )
}